
import * as React from "react";

import "./dialog-edit-image.scss";

import {consumeContext} from "@Common/react/context";
import {ComponentLink,
        Link,
        makeChain}      from "@Common/react/cst2";
import {UseState}       from "@Common/react/use-state";
import {Types}          from "@Common/types";
import {ModalService3}  from "@Common/ui-components/modal-service-3/modal-service-3";
import {ButtonSpinner}  from "@Common/ui-components/loading-spinners/button-spinner";

import {ErrorMessage} from "./error-message";


export const DialogEditImage = ({
    next: rootNext,
    images,
    imageId,
    orderId           = 0,
    onApplyImageEdit  = null,
}:{
    next              : ComponentLink<typeof ModalService3<{}>, "rootNext">,
    images            : Types.Apis.Models.GalleryImage[],
    imageId           : number,
    orderId          ?: number,
    onApplyImageEdit ?: (imageId:number, imageName:string) => Promise<string | void>,
}) => (

    makeChain<JSX.Element>()

    .then(...consumeContext("apis"))

    // set modal attributes
    .then("modal", (_, next:ComponentLink<typeof ModalService3>) => ModalService3({
        next,
        rootNext,
        title: "Edit Photo",
        width: 400,
        xToClose: false,
        ignorePassiveClose: true,
        contentClassName: "dialog-ej6",
        xdQaName: "Edit Photo",
    }))

    // set state vars
    .then("state", (_, next:ComponentLink<typeof UseState<{
        imageName        : string,
        isImageUpdating  : boolean,
        errorMessage     : string,
    }>>) => UseState({
        defaultValue: {
            imageName: images?.find(x => x.id === imageId)?.name,
            isImageUpdating: false,
            errorMessage: "",
        },
        next
    }))

    // create function for applying changes
    .then("onProceed", ({state, apis, modal}, next:Link<JSX.Element, (event:React.FormEvent<HTMLFormElement>|React.MouseEvent<HTMLButtonElement>) => void>) => next(async (event) => {

        event.preventDefault(); // prevent default so that pressing enter to submit the form does not cause full page refresh
        const newImageName = state.value.imageName?.trim();

        // if save without any changes, close window
        if (!newImageName || newImageName === images.find(x => x.id === imageId).name) return modal.close();

        // validate that the name is not already in use by another image - CC doesn't allow identical names
        // with case differences, so using localeCompare to check
        if (images.find(x => x.id !== imageId && x.name.toLowerCase() === newImageName.toLowerCase())) {
            return state.onChange({
                ...state.value,
                errorMessage: "Your photo name is already in use by another photo in your album.",
            });
        }

        // set state to updating status
        state.onChange({...state.value, isImageUpdating: true});

        // run apply callback if provided
        if (onApplyImageEdit) {
            const errorMessage = await onApplyImageEdit(imageId, newImageName);
            if (errorMessage) {
                return state.onChange({
                    ...state.value,
                    errorMessage,
                });
            }
        }

        // close the modal with success response
        modal.close(true);

    }))

    // render modal contents
    .then(({state, modal, onProceed}) => (<>
        <div className="content">
            <div className="message">
                <ErrorMessage text={state.value.errorMessage}/>
                <form className="edit-form" onSubmit={onProceed}>
                    <div className="form-group">
                        <div className="control-label">Name</div>
                        <input
                            type="text"
                            xd-qa-type="text_field"
                            xd-qa-name="ccgallery photo album edit name"
                            value={state.value.imageName}
                            onChange={(e) => {
                                state.onChange({
                                    ...state.value,
                                    imageName: e.target.value,
                                    errorMessage: "",
                                });
                            }}
                            autoFocus
                        />
                    </div>
                </form>
            </div>
        </div>
        <div className="footer">
            <button
                className="cancel"
                xd-qa-type="button" xd-qa-name="ccgallery photo album edit modal cancel"
                onClick={() => modal.close()}
            >
                Cancel
            </button>
            <button
                className="proceed"
                xd-qa-type="button" xd-qa-name="ccgallery photo album edit modal save"
                onClick={onProceed}
                disabled={state.value.isImageUpdating}
            >
                {!state.value.isImageUpdating ? "Save" : (
                    <ButtonSpinner spinnerColor="#ffffff"/>
                )}
            </button>
        </div>
    </>))

    .run()

);
